/**
 * Single source of truth for the three-book series. Every page that surfaces
 * a book pulls from this file. External URLs always go to shariqrefai.com
 * (the books are not sold on AnxietyResource). Site-internal URLs are the
 * detail page on this site.
 */
import { SITE } from "./site";

export interface Book {
  slug: string;
  title: string;
  cleanTitle: string;
  subtitle: string;
  cover: string;
  coverAlt: string;
  externalUrl: string;
  internalPath: string;
  shortDescription: string;
  cardBlurb: string;
  metaDescription: string;
  metaTitle: string;
  /** ISBN-13 (with hyphens) for the paperback edition. Undefined for titles
   *  that have not yet been assigned an ISBN. */
  isbn?: string;
  /** Print/digital format string for human display and `bookFormat` schema. */
  bookFormat?: "Paperback" | "Hardcover" | "EBook" | "AudiobookFormat";
  /** Legal publisher entity (used in Book JSON-LD `publisher.name`). The
   *  display copy elsewhere uses the brand "shrinkMD Publishing"; this is the
   *  authoritative legal name tied to the ISBN. */
  publisherLegalName?: string;
}

export const BOOKS: Book[] = [
  {
    slug: "your-mind-is-full-of-shit",
    title: "Your Mind Is Full of Sh*t",
    cleanTitle: "Your Mind Is Full of Sh*t",
    subtitle:
      "A Psychiatrist's Guide to Understanding Overthinking, Anxiety, and the Lies in Your Head",
    cover: "/images/books/your-mind-is-full-of-shit.webp",
    coverAlt:
      "Book cover for 'Your Mind Is Full of Sh*t' by Shariq Refai, MD, MBA, set against a teal background.",
    externalUrl: "https://shariqrefai.com/books/your-mind-is-full-of-shit",
    internalPath: "/resources/books/your-mind-is-full-of-shit",
    shortDescription:
      "A psychiatrist's guide to overthinking, anxiety, and the four ideas most adults absorbed before they noticed.",
    cardBlurb:
      "The framework book. It examines four ideas most adults absorb before they realize they have absorbed them, the lies of control, productivity, positivity, and happiness, and shows how each one shapes anxiety, overthinking, and emotional exhaustion. The writing is plain, the references are clinical, and the practices are small enough to fit into a real week. It is for readers who have already tried the apps, the worksheets, and the bestsellers and still cannot quiet the noise.",
    metaTitle:
      "Your Mind Is Full of Sh*t by Shariq Refai, MD, MBA | AnxietyResource",
    metaDescription:
      "A psychiatrist's guide to overthinking, anxiety, and the four ideas most adults absorbed without choosing them. Book overview by Shariq Refai, MD, MBA.",
    isbn: "979-8-950653-00-1",
    bookFormat: "Paperback",
    publisherLegalName: "shrinkMD Publishing LLC",
  },
  {
    slug: "your-mind-is-full-of-shit-workbook",
    title: "Your Mind Is Full of Sh*t: The Workbook",
    cleanTitle: "The Workbook",
    subtitle:
      "A Psychiatrist's Companion Guide for Noticing Your Own Patterns",
    cover: "/images/books/your-mind-is-full-of-shit-workbook.webp",
    coverAlt:
      "Cover placeholder for 'Your Mind Is Full of Sh*t: The Workbook' by Shariq Refai, MD, MBA.",
    externalUrl:
      "https://shariqrefai.com/books/your-mind-is-full-of-shit-workbook",
    internalPath:
      "/resources/books/your-mind-is-full-of-shit-workbook",
    shortDescription:
      "The companion workbook to the main book. Psychiatrist-designed reflection exercises and observation prompts for anxiety and overthinking patterns.",
    cardBlurb:
      "The companion to the main book. A structured set of reflection exercises, observation prompts, and tracking tools written the way a clinician would run them in session, on paper a reader can use themselves. The workbook helps the framework land in a real Tuesday, not just in the chapter. It can be used alone, alongside the main book, or with a clinician.",
    metaTitle:
      "Your Mind Is Full of Sh*t: The Workbook | AnxietyResource",
    metaDescription:
      "The companion workbook to Dr. Shariq Refai's book: psychiatrist-designed reflection exercises for noticing your own anxiety and overthinking patterns.",
  },
  {
    slug: "the-havoc-in-your-head",
    title: "The Havoc in Your Head",
    cleanTitle: "The Havoc in Your Head",
    subtitle:
      "A Psychiatrist's Reset for Anxiety, Overthinking, Panic, and the Thoughts That Won't Let Go",
    cover: "/images/books/the-havoc-in-your-head.webp",
    coverAlt:
      "Cover placeholder for 'The Havoc in Your Head' by Shariq Refai, MD, MBA.",
    externalUrl: "https://shariqrefai.com/books/the-havoc-in-your-head",
    internalPath: "/resources/books/the-havoc-in-your-head",
    shortDescription:
      "A psychiatrist's reset for anxiety, panic, overthinking, and intrusive thoughts in the moment they happen. The in-the-moment companion to Your Mind Is Full of Sh*t.",
    cardBlurb:
      "The in-the-moment book. Most popular mental health advice is written for a calm brain and handed to a panicked one. This book reorders the sequence: body first, then sensory orientation, then naming, then choice. The shrinkMD Reset is built to run in under a minute when the chest is already tight and the mind is already racing. It is the response companion to Your Mind Is Full of Sh*t, and stands on its own.",
    metaTitle:
      "The Havoc in Your Head by Shariq Refai, MD, MBA | AnxietyResource",
    metaDescription:
      "A psychiatrist's in-the-moment reset for anxiety, panic, overthinking, and intrusive thoughts. Overview of the book by Shariq Refai, MD, MBA.",
  },
];

export function getBook(slug: string): Book | undefined {
  return BOOKS.find((b) => b.slug === slug);
}

export function bookCanonical(book: Book): string {
  return `${SITE.url}${book.internalPath}`;
}
